/**
 * Compact JSON format for AI-generated workflows.
 * Expands abbreviated keys back to full WorkflowState keys.
 */

const typeMap: Record<string, string> = {
  ge: 'general',
  co: 'condition',
  cu: 'create-user',
  dm: 'disable-mailbox',
  d3: 'delete-m365',
  se: 'send-email',
  wh: 'webhook',
  ag: 'add-to-group',
};

const portMap: Record<string, string> = {
  t: 'top',
  tr: 'top-right',
  r: 'right',
  br: 'bottom-right',
  b: 'bottom',
  bl: 'bottom-left',
  l: 'left',
  tl: 'top-left',
};

function expandPort(v: string | undefined): string | undefined {
  if (!v) return v;
  return portMap[v] ?? v;
}

function expandType(v: string): string {
  return typeMap[v] ?? v;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function expandBlock(b: any): any {
  const cfg = b.cfg ?? b.config ?? {};
  // Fallback name: derive from block id when the AI omits the
  // config.name field. WorkflowBlockSchema requires `name` to have
  // ≥1 character, so an empty default would fail validation and
  // bounce the whole import. The id is always present, so it's a
  // safe last-resort label the user can rename in-place.
  const fallbackName = typeof b.id === 'string' && b.id.length > 0 ? b.id : 'Block';
  return {
    id: b.id,
    type: expandType(b.t ?? b.type ?? 'general'),
    x: b.x,
    y: b.y,
    config: {
      name: cfg.n ?? cfg.name ?? fallbackName,
      ...(cfg.d != null || cfg.description != null ? { description: cfg.d ?? cfg.description } : {}),
      ...(cfg.dd != null || cfg.detailedDescription != null ? { detailedDescription: cfg.dd ?? cfg.detailedDescription } : {}),
      ...(cfg.i != null || cfg.icon != null ? { icon: cfg.i ?? cfg.icon } : {}),
      ...(cfg.to != null ? { to: cfg.to } : {}),
      ...(cfg.subject != null || cfg.s != null ? { subject: cfg.s ?? cfg.subject } : {}),
    },
    color: b.c ?? b.clr ?? b.color ?? '#6366f1',
    ...(b.m != null || b.mtime != null ? { mtime: b.m ?? b.mtime } : {}),
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function expandConnector(c: any): any {
  return {
    id: c.id,
    fromId: c.f ?? c.fromId,
    toId: c.to ?? c.toId,
    ...(c.fp != null || c.fromPort != null ? { fromPort: expandPort(c.fp ?? c.fromPort) } : {}),
    ...(c.tp != null || c.toPort != null ? { toPort: expandPort(c.tp ?? c.toPort) } : {}),
    ...(c.c != null || c.clr != null || c.color != null ? { color: c.c ?? c.clr ?? c.color } : {}),
    ...(c.bd != null || c.bidirectional != null ? { bidirectional: c.bd ?? c.bidirectional } : {}),
    ...(c.pl != null || c.pulse != null ? { pulse: c.pl ?? c.pulse } : {}),
    ...(c.plc != null || c.pulseColor != null ? { pulseColor: c.plc ?? c.pulseColor } : {}),
    // AI / cloud-emit:ade waypoints droppas tyst vid expand. Manuell
    // dubbelklick-add på linjen är ENDA tillåtna källan — auto-injection
    // från AI-prompts eller stored entity_details ska aldrig flöda in.
    // Fältet behålls i Connector-typen för in-memory-redigering, men
    // import-vägen är stängd.
    ...(c.lb != null || c.label != null ? { label: c.lb ?? c.label } : {}),
    ...(c.m != null || c.mtime != null ? { mtime: c.m ?? c.mtime } : {}),
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function expandGroup(g: any): any {
  return {
    id: g.id,
    blockIds: g.bIds ?? g.blockIds ?? [],
    color: g.c ?? g.clr ?? g.color ?? '#8b5cf6',
    ...(g.n != null || g.name != null ? { name: g.n ?? g.name } : {}),
    ...(g.lk != null || g.locked != null ? { locked: g.lk ?? g.locked } : {}),
    ...(g.col != null || g.collapsed != null ? { collapsed: g.col ?? g.collapsed } : {}),
    ...(g.cx != null || g.collapsedX != null ? { collapsedX: g.cx ?? g.collapsedX } : {}),
    ...(g.cy != null || g.collapsedY != null ? { collapsedY: g.cy ?? g.collapsedY } : {}),
    ...(g.pg !== undefined || g.parentGroupId !== undefined || g.parent !== undefined ? { parentGroupId: g.pg ?? g.parentGroupId ?? g.parent ?? null } : {}),
    ...(g.bl != null || g.boundsLeft != null ? { boundsLeft: g.bl ?? g.boundsLeft } : {}),
    ...(g.bt != null || g.boundsTop != null ? { boundsTop: g.bt ?? g.boundsTop } : {}),
    ...(g.br != null || g.boundsRight != null ? { boundsRight: g.br ?? g.boundsRight } : {}),
    ...(g.bb != null || g.boundsBottom != null ? { boundsBottom: g.bb ?? g.boundsBottom } : {}),
    ...(g.m != null || g.mtime != null ? { mtime: g.m ?? g.mtime } : {}),
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function expandCrossSheetConnector(c: any): any {
  return {
    id: c.id,
    fromSheetIndex: c.fsi ?? c.fromSheetIndex,
    toSheetIndex: c.tsi ?? c.toSheetIndex,
    fromBlockId: c.fbid ?? c.fromBlockId,
    toBlockId: c.tbid ?? c.toBlockId,
    ...(c.fp != null || c.fromPort != null ? { fromPort: expandPort(c.fp ?? c.fromPort) } : {}),
    ...(c.tp != null || c.toPort != null ? { toPort: expandPort(c.tp ?? c.toPort) } : {}),
    ...(c.lb != null || c.label != null ? { label: c.lb ?? c.label } : {}),
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function expandSheet(s: any): any {
  return {
    id: s.id,
    name: s.name ?? s.n ?? 'Sheet',
    blocks: (s.b ?? s.blocks ?? []).map(expandBlock),
    connectors: (s.cn ?? s.connectors ?? []).map(expandConnector),
    groups: (s.g ?? s.groups ?? []).map(expandGroup),
    ...(s.meta ? { meta: s.meta } : {}),
  };
}

/**
 * Detect if a parsed JSON object uses the compact format.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isCompactFormat(data: any): boolean {
  if (!data || typeof data !== 'object') return false;
  return Array.isArray(data.b) || Array.isArray(data.cn) || data.co === 'center'
    || Array.isArray(data.sh); // compact multi-sheet
}

/**
 * Detect the "lean-1" AI output shape: blocks have FLAT `name`,
 * `description`, `icon` at the top level (no nested `config`) and
 * connectors use `from`/`to` (not `fromId`/`toId`). The AI invents
 * this format spontaneously despite the system prompt — instead of
 * rejecting the import we normalise it.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isLeanFormat(data: any): boolean {
  if (!data || typeof data !== 'object') return false;
  if (data.format === 'lean-1') return true;
  // Sample the first block we can find — single-sheet at root or
  // the first non-empty sheet's first block. Empty fall-through
  // is intentional; no blocks ⇒ nothing to normalise.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let sample: any = null;
  if (Array.isArray(data.blocks) && data.blocks.length > 0) {
    sample = data.blocks[0];
  } else if (Array.isArray(data.sheets)) {
    for (const sheet of data.sheets) {
      if (sheet && Array.isArray(sheet.blocks) && sheet.blocks.length > 0) {
        sample = sheet.blocks[0];
        break;
      }
    }
  }
  if (!sample || typeof sample !== 'object') return false;
  return sample.config === undefined && typeof sample.name === 'string';
}

/**
 * Normalise the "lean-1" shape into the standard WorkflowState shape:
 *   - lift block-level `name`/`description`/`icon`/`to`/`subject`
 *     into a nested `config: {}` object
 *   - rename connector `from`/`to` → `fromId`/`toId`
 *
 * Pure: returns a new object; pass-through when `data` doesn't look
 * lean. Composes safely with `expandCompactWorkflow` (run lean first,
 * compact second).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function normaliseLeanFormat(data: any): any {
  if (!isLeanFormat(data)) return data;

  // Multi-sheet
  if (Array.isArray(data.sheets)) {
    return {
      ...data,
      sheets: data.sheets.map(normaliseLeanSheet),
    };
  }
  // Single-sheet
  return {
    ...data,
    blocks: Array.isArray(data.blocks) ? data.blocks.map(normaliseLeanBlock) : data.blocks,
    connectors: Array.isArray(data.connectors) ? data.connectors.map(normaliseLeanConnector) : data.connectors,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normaliseLeanSheet(s: any): any {
  if (!s || typeof s !== 'object') return s;
  return {
    ...s,
    blocks: Array.isArray(s.blocks) ? s.blocks.map(normaliseLeanBlock) : s.blocks,
    connectors: Array.isArray(s.connectors) ? s.connectors.map(normaliseLeanConnector) : s.connectors,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normaliseLeanBlock(b: any): any {
  if (!b || typeof b !== 'object') return b;
  // Already has a nested config? Trust it.
  if (b.config && typeof b.config === 'object') return b;
  const { name, description, detailedDescription, icon, to, subject, ...rest } = b;
  const cfg: Record<string, unknown> = {};
  if (name !== undefined) cfg.name = name;
  if (description !== undefined) cfg.description = description;
  if (detailedDescription !== undefined) cfg.detailedDescription = detailedDescription;
  if (icon !== undefined) cfg.icon = icon;
  if (to !== undefined) cfg.to = to;
  if (subject !== undefined) cfg.subject = subject;
  return { ...rest, config: cfg };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normaliseLeanConnector(c: any): any {
  if (!c || typeof c !== 'object') return c;
  if (c.fromId !== undefined && c.toId !== undefined) return c;
  const { from, to, ...rest } = c;
  return {
    ...rest,
    ...(from !== undefined ? { fromId: from } : {}),
    ...(to !== undefined ? { toId: to } : {}),
  };
}

/**
 * Expand a compact workflow JSON object into a full WorkflowState-compatible object.
 * Supports both single-sheet and multi-sheet compact formats.
 * If the data is already in full format, it passes through unchanged.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function expandCompactWorkflow(data: any): any {
  if (!isCompactFormat(data)) return data;

  // Optional schema-version tag: `v` on compact, `schemaVersion` on full.
  // Neither Zod schema declares it, so we don't include it in the
  // returned shape — callers extract it explicitly via `compactSchemaVersion()`
  // before passing data into `parseWorkflowState(data, version)`.

  // Multi-sheet compact format
  if (Array.isArray(data.sh) || Array.isArray(data.sheets)) {
    return {
      name: data.name ?? data.n ?? '',
      sheets: (data.sh ?? data.sheets ?? []).map(expandSheet),
      crossSheetConnectors: (data.csc ?? data.crossSheetConnectors ?? []).map(expandCrossSheetConnector),
      activeSheetIndex: data.asi ?? data.activeSheetIndex ?? 0,
      ...(data.co === 'center' || data.coordinateOrigin === 'center'
        ? { coordinateOrigin: 'center' as const }
        : {}),
    };
  }

  // Single-sheet compact format
  return {
    name: data.name ?? data.n ?? '',
    blocks: (data.b ?? data.blocks ?? []).map(expandBlock),
    connectors: (data.cn ?? data.connectors ?? []).map(expandConnector),
    groups: (data.g ?? data.groups ?? []).map(expandGroup),
    ...(data.co === 'center' || data.coordinateOrigin === 'center'
      ? { coordinateOrigin: 'center' as const }
      : {}),
  };
}

/**
 * Extract the `schema_version` that compact data was authored at, or
 * `undefined` if the tag is absent (typical for in-memory data not
 * round-tripped through DB). Accepts both the compact `v` field and
 * the full `schemaVersion` field to keep call-sites uniform.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function compactSchemaVersion(data: any): number | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const v = data.v ?? data.schemaVersion;
  return typeof v === 'number' ? v : undefined;
}

// ─── Compact encoder (inverse of expandCompactWorkflow) ────────────
//
// Emit the compact shape that the AI-assistant examples teach. The
// encoder is lossy only for unknown enum values (unknown block types
// fall back to the full name). Round-trip with `expandCompactWorkflow`
// is tested in `compactWorkflow.test.ts`.

const typeReverseMap: Record<string, string> = Object.fromEntries(
  Object.entries(typeMap).map(([k, v]) => [v, k]),
);
const portReverseMap: Record<string, string> = Object.fromEntries(
  Object.entries(portMap).map(([k, v]) => [v, k]),
);

function shortenType(t: string | undefined): string | undefined {
  if (t == null) return t;
  return typeReverseMap[t] ?? t;
}

function shortenPort(p: string | undefined): string | undefined {
  if (p == null) return p;
  return portReverseMap[p] ?? p;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function compactBlock(b: any): any {
  const cfg = b.config ?? {};
  const out: Record<string, unknown> = {
    id: b.id,
    t: shortenType(b.type ?? 'general'),
    x: b.x,
    y: b.y,
    cfg: {
      n: cfg.name ?? '',
      ...(cfg.description != null ? { d: cfg.description } : {}),
      ...(cfg.detailedDescription != null ? { dd: cfg.detailedDescription } : {}),
      ...(cfg.icon != null ? { i: cfg.icon } : {}),
      ...(cfg.to != null ? { to: cfg.to } : {}),
      ...(cfg.subject != null ? { s: cfg.subject } : {}),
    },
    c: b.color ?? '#6366f1',
  };
  if (b.mtime != null) out.m = b.mtime;
  return out;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function compactConnector(c: any): any {
  const out: Record<string, unknown> = {
    id: c.id,
    f: c.fromId,
    to: c.toId,
  };
  if (c.fromPort != null) out.fp = shortenPort(c.fromPort);
  if (c.toPort != null) out.tp = shortenPort(c.toPort);
  if (c.color != null) out.c = c.color;
  if (c.bidirectional != null) out.bd = c.bidirectional;
  if (c.pulse != null) out.pl = c.pulse;
  if (c.pulseColor != null) out.plc = c.pulseColor;
  // Manuella waypoints persisteras INTE via compact-export — de är
  // session-lokala. Annars skulle copy/paste av JSON eller AI-roundtrip
  // återinföra waypoint-data som vi i nästa import-cykel ändå droppar.
  if (c.label != null) out.lb = c.label;
  if (c.mtime != null) out.m = c.mtime;
  return out;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function compactGroup(g: any): any {
  const out: Record<string, unknown> = {
    id: g.id,
    bIds: g.blockIds ?? [],
    c: g.color ?? '#8b5cf6',
  };
  if (g.name != null) out.n = g.name;
  if (g.locked != null) out.lk = g.locked;
  if (g.collapsed != null) out.col = g.collapsed;
  if (g.collapsedX != null) out.cx = g.collapsedX;
  if (g.collapsedY != null) out.cy = g.collapsedY;
  if (g.parentGroupId !== undefined) out.pg = g.parentGroupId;
  if (g.boundsLeft != null) out.bl = g.boundsLeft;
  if (g.boundsTop != null) out.bt = g.boundsTop;
  if (g.boundsRight != null) out.br = g.boundsRight;
  if (g.boundsBottom != null) out.bb = g.boundsBottom;
  if (g.mtime != null) out.m = g.mtime;
  return out;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function compactSheet(s: any): any {
  const out: Record<string, unknown> = {
    id: s.id,
    name: s.name ?? 'Sheet',
    b: (s.blocks ?? []).map(compactBlock),
    cn: (s.connectors ?? []).map(compactConnector),
    g: (s.groups ?? []).map(compactGroup),
  };
  if (s.meta) out.meta = s.meta;
  return out;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function compactCrossSheetConnector(c: any): any {
  const out: Record<string, unknown> = {
    id: c.id,
    fsi: c.fromSheetIndex,
    tsi: c.toSheetIndex,
    fbid: c.fromBlockId,
    tbid: c.toBlockId,
  };
  if (c.fromPort != null) out.fp = shortenPort(c.fromPort);
  if (c.toPort != null) out.tp = shortenPort(c.toPort);
  if (c.label != null) out.lb = c.label;
  return out;
}

/**
 * Encode a full WorkflowState (single-sheet or multi-sheet) into the
 * compact AI-friendly format. Round-trips through
 * `expandCompactWorkflow` without data loss for all known enums; unknown
 * block types / port names pass through verbatim.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function compactWorkflowState(data: any): any {
  if (data == null || typeof data !== 'object') return data;

  // Multi-sheet workflow
  if (Array.isArray(data.sheets)) {
    return {
      name: data.name ?? '',
      sh: data.sheets.map(compactSheet),
      csc: (data.crossSheetConnectors ?? []).map(compactCrossSheetConnector),
      asi: data.activeSheetIndex ?? 0,
      ...(data.coordinateOrigin === 'center' ? { co: 'center' } : {}),
    };
  }

  // Single-sheet workflow
  return {
    name: data.name ?? '',
    b: (data.blocks ?? []).map(compactBlock),
    cn: (data.connectors ?? []).map(compactConnector),
    g: (data.groups ?? []).map(compactGroup),
    ...(data.coordinateOrigin === 'center' ? { co: 'center' } : {}),
  };
}
